/**
 * Ability sound-effect loader (#608) — resolves an ability id to a bundled
 * `.mp3` URL, entirely client-side (presentation layer). The ability-sound
 * counterpart of `render/championSprites.ts` (id → slug → bundled asset,
 * same glob + fallback shape) and the second half of `tools/sound-gen`'s
 * generation pipeline (that tool produces the files this loader resolves).
 *
 * BOUNDARY: sound is cosmetic/presentation and MUST NOT touch the sim or any
 * hash. `ABILITY_SCHEMA` (packages/protocol) carries no sound field — ISSUE-608
 * open question 2 resolved on SLUG-KEYED linkage (option a), zero schema
 * change, mirroring how `championSprites.ts` resolves a champion's slug to
 * its sprite sheet with zero protocol involvement. Deleting an `.mp3` returns
 * that ability to the existing procedural `"ability"` cue (the rip-out
 * contract, same as sprites falling back to the procedural body draw).
 * (#795 widened the WIRE `damage` event with the responsible ability ID so the
 * client can pick an IMPACT sound — an id, never sound data; this boundary is
 * unchanged.)
 *
 * ASSET RESOLUTION: an ability's sound lives at
 * `packages/content/assets/abilities/<slug>/<slug>.mp3`. Vite's
 * `import.meta.glob` maps it to a content-hashed URL at build time. Absent
 * file → `null` → the caller falls back to `sound.play("ability")`.
 *
 * COVERAGE: 15 Mage-kit slugs ship a committed `.mp3` today; every other
 * ability resolves to `null` and gets the procedural cue. The rip-out contract
 * is therefore per-ability, not all-or-nothing — see `abilitySounds.test.ts`'s
 * `COMMITTED_SOUND_SLUGS` for the authoritative list.
 *
 * WHO CALLS THIS: `sound/impactAudio.ts` (#795), when a `damage` event lands.
 * It is deliberately NOT called from the cast keypress any more — cast-time
 * audio was speculative and fired at the wrong moment.
 */

import type { AbilityId } from "@pit/protocol";
import { ABILITY_META_BY_ID } from "../champions/abilityCatalog.js";

/**
 * Build-time map of every committed ability sound `.mp3` → its bundled URL.
 * Eager + `?url` so each entry is a ready string. Glob base is the content
 * assets folder (outside the client package; Vite `server.fs.allow` includes
 * the repo root). Indexed by the `<slug>` folder segment.
 *
 * IMPORTANT: `import.meta.glob(...)` MUST be written as this literal call
 * expression — see the #181 rationale already documented in
 * `championSprites.ts`/`abilityIcons.ts`: Vite only transforms the literal
 * form, so a cast/aliased `import.meta.glob` silently leaves the map empty at
 * build time.
 */
function loadSoundUrlMap(): ReadonlyMap<string, string> {
  const modules = import.meta.glob<string>(
    "../../../../packages/content/assets/abilities/**/*.mp3",
    { eager: true, query: "?url", import: "default" },
  );
  return bySlug(modules);
}

/** Index glob results by the `<slug>` folder segment (first sorted file wins,
 *  mirroring `championSprites.ts`'s `bySlug`). */
function bySlug(modules: Readonly<Record<string, string>>): ReadonlyMap<string, string> {
  const map = new Map<string, string>();
  for (const path of Object.keys(modules).sort()) {
    const slug = slugFromAbilityAssetPath(path);
    const value = modules[path];
    if (slug !== null && value !== undefined && !map.has(slug)) map.set(slug, value);
  }
  return map;
}

/**
 * Extract the `<slug>` folder segment from a glob module path.
 * e.g. ".../assets/abilities/frost-nova/frost-nova.mp3" → "frost-nova".
 * Returns null if the path doesn't sit under an `abilities/` segment.
 *
 * Exported (test-only) so this pure extraction is directly unit-testable
 * against synthetic paths without depending on which sounds happen to be
 * committed at test time (mirrors `abilityIcons.ts`'s `iconKeyFromPath`).
 */
export function slugFromAbilityAssetPath(path: string): string | null {
  const marker = "/abilities/";
  const idx = path.lastIndexOf(marker);
  if (idx === -1) return null;
  const tail = path.slice(idx + marker.length);
  const seg = tail.split("/")[0];
  return seg === undefined || seg.length === 0 ? null : seg;
}

const SOUND_URL_BY_SLUG: ReadonlyMap<string, string> = loadSoundUrlMap();

/** A resolved ability sound: the bundled `.mp3` url + the slug it resolved. */
export interface AbilitySound {
  readonly slug: string;
  readonly url: string;
}

/**
 * Resolve `abilityId`'s committed sound (bundled url + its slug), or `null`
 * when: the ability id is unknown, OR no `.mp3` is committed for its slug.
 * Callers fall back to `sound.play("ability")` on `null` — the rip-out
 * contract, never a crash.
 */
export function resolveAbilitySound(abilityId: AbilityId): AbilitySound | null {
  const meta = ABILITY_META_BY_ID.get(abilityId);
  if (meta === undefined) return null;
  const url = SOUND_URL_BY_SLUG.get(meta.slug);
  return url === undefined ? null : { slug: meta.slug, url };
}

/** Ability ids that have a committed sound file — for tests/diagnostics
 *  (mirrors `championSprites.ts`'s `KNOWN_SPRITE_CHAMPION_IDS`). */
export const KNOWN_ABILITY_SOUND_IDS: readonly AbilityId[] = Array.from(
  ABILITY_META_BY_ID.values(),
)
  .filter((meta) => SOUND_URL_BY_SLUG.has(meta.slug))
  .map((meta) => meta.id);
